import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import useLenis from "@/hooks/useLenis";

const AuthShell = ({ eyebrow, title, subtitle, children, altPrompt, altLabel, altHref }) => {
  useLenis();
  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden">
      {/* Backdrop logo */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.045]">
        <Logo showWordmark={false} className="w-[80vw] max-w-[900px] h-auto" />
      </div>
      <div className="absolute inset-0 grain-overlay opacity-[0.06] pointer-events-none" />

      <div className="relative min-h-screen grid grid-cols-1 md:grid-cols-2">
        {/* Left panel */}
        <div className="hidden md:flex flex-col justify-between p-12 border-r border-white/10">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <Logo showWordmark={false} className="h-10 w-10" />
            <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
              MIND. BODY. SOUL.
            </div>
          </Link>

          <div>
            <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-4">
              §MEMBER
            </div>
            <h2 className="font-serif text-white text-5xl leading-[0.95]">
              The dispatch,
              <br />
              <span className="italic text-silver-100">the covenant,</span>
              <br />
              the numbered.
            </h2>
            <p className="mt-6 text-white/60 max-w-md leading-[1.75] text-[14px]">
              Sign in to track your enrolled programs, your Turn 180 progress,
              and reserve upcoming apparel drops before they leave the workshop.
            </p>
          </div>

          <div className="font-mono text-[10px] tracking-[0.32em] text-white/40">
            EST. XXV &nbsp;·&nbsp; N° 000247+
          </div>
        </div>

        {/* Right panel — form */}
        <div className="flex items-center justify-center px-6 py-16 md:py-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md"
          >
            <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-4">
              {eyebrow}
            </div>
            <h1 className="font-serif text-white text-4xl md:text-5xl leading-[1.02] tracking-[-0.01em]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-4 text-white/60 text-[14px] leading-[1.7]">
                {subtitle}
              </p>
            )}
            <div className="mt-10">{children}</div>
            {altPrompt && (
              <div className="mt-8 text-white/55 text-[13px]">
                {altPrompt}{" "}
                <Link
                  to={altHref}
                  className="text-silver-100 hover:text-white underline underline-offset-4"
                >
                  {altLabel}
                </Link>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export const Login = () => {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast.error("Email and password required.");
      return;
    }
    setBusy(true);
    const res = await login(form.email.trim(), form.password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}.`);
      navigate(from, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell
      eyebrow="§SIGN IN"
      title={<>Return to <span className="italic text-silver-100">the order.</span></>}
      subtitle="Sign in with your enlisted email and password."
      altPrompt="Not yet numbered?"
      altLabel="Create an account →"
      altHref="/register"
    >
      <form onSubmit={submit} data-testid="login-form" className="space-y-6">
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">EMAIL</span>
          <input
            type="email"
            required
            data-testid="login-email-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="you@dispatch.com"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">PASSWORD</span>
          <input
            type="password"
            required
            data-testid="login-password-input"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="••••••••"
          />
        </label>

        <button
          type="submit"
          data-testid="login-submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-colors duration-500 disabled:opacity-60"
        >
          <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
            {busy ? "Verifying…" : "Sign In"}
          </span>
          <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 6H11M11 6L6 1M11 6L6 11" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </span>
        </button>
      </form>
    </AuthShell>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    const res = await register(form.name.trim(), form.email.trim(), form.password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}. Your number begins today.`);
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell
      eyebrow="§ENLIST"
      title={<>Take your <span className="italic text-silver-100">number.</span></>}
      subtitle="Create an account to unlock the member dashboard, program history, and dispatch archive."
      altPrompt="Already numbered?"
      altLabel="Sign in →"
      altHref="/login"
    >
      <form onSubmit={submit} data-testid="register-form" className="space-y-6">
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">NAME</span>
          <input
            type="text"
            required
            data-testid="register-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="Your full name"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">EMAIL</span>
          <input
            type="email"
            required
            data-testid="register-email-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="you@dispatch.com"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">PASSWORD</span>
          <input
            type="password"
            required
            minLength={6}
            data-testid="register-password-input"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="6+ characters"
          />
        </label>

        <button
          type="submit"
          data-testid="register-submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-colors duration-500 disabled:opacity-60"
        >
          <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
            {busy ? "Sealing…" : "Create Account"}
          </span>
          <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 6H11M11 6L6 1M11 6L6 11" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </span>
        </button>
      </form>
    </AuthShell>
  );
};

export default Login;
